<script setup>
import { ref, reactive, computed, watch, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ColorPicker from 'dashboard/components-next/colorpicker/ColorPicker.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import { buildOrderedStatusOptions } from 'dashboard/routes/dashboard/planning/events/statusOptions';
import { resolveStatusOptionColor } from 'dashboard/routes/dashboard/planning/events/useEventStageColors';
import {
  ALLOCATION_CATEGORIES,
  CUSTOM_COLOR_SWATCHES,
  DISCO_COLOR,
  getCategoryConfig,
} from './allocationCategories';
import {
  readRecentAllocationColors,
  pushRecentAllocationColor,
} from './recentAllocationColors';
import { coerceAllocationInteger } from './allocationPayload';
import {
  clampAllocationDateTimeFields,
  formatLocalDate,
  toLocalTime,
} from './allocationDateTimeFields';
import { formatTime } from 'shared/helpers/formatTime';
import EventLostCancellationModal from 'dashboard/routes/dashboard/planning/events/EventLostCancellationModal.vue';
import { useLostStageUpdate } from 'dashboard/routes/dashboard/planning/events/useLostStageUpdate';

const props = defineProps({
  allocation: {
    type: Object,
    default: null,
  },
  resource: {
    type: Object,
    required: true,
  },
  position: {
    type: Object,
    default: () => ({ x: 0, y: 0 }),
  },
  compact: {
    type: Boolean,
    default: false,
  },
  embedded: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'cancel', 'update', 'delete']);

const TIME_STEP_MINUTES = 15;

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();
const {
  showLostCancellationModal,
  pendingLostUpdate,
  lostCancellationSaving,
  requestStageUpdate,
  confirmLostCancellation,
  cancelLostCancellation,
} = useLostStageUpdate();

const calendarEventShowQuery = () => {
  const query = { from: 'calendar' };
  if (route.params.date) query.fromDate = route.params.date;
  const hash = route.hash?.replace(/^#/, '');
  if (hash) query.fromHash = hash;
  return query;
};

const modalRef = ref(null);
const title = ref('');
const startYear = ref(new Date().getFullYear());
const startMonth = ref(new Date().getMonth());
const startDay = ref(new Date().getDate());
const startTime = ref('09:00');
const endYear = ref(new Date().getFullYear());
const endMonth = ref(new Date().getMonth());
const endDay = ref(new Date().getDate());
const endTime = ref('10:00');
const category = ref('event');
const status = ref('draft');
const managerId = ref(null);
const customColor = ref(null);
const isVisible = ref(false);

// Context Menu
const showContextMenu = ref(false);
const contextMenuPosition = ref({ x: 0, y: 0 });
const activeSubmenu = ref(null); // Track which submenu is open
const statusSubmenuRef = ref(null);
const managerSubmenuRef = ref(null);
const statusButtonRef = ref(null);
const managerButtonRef = ref(null);
const submenuPositions = ref({ status: 'right', manager: 'right' });
const availableStatusOptions = ref([]);
const isHydratingEventData = ref(false);

const resolvedResourceId = computed(
  () => props.resource?.id ?? props.allocation?.resource_id
);

const DEFAULT_SYSTEM_STATUSES = [
  'draft',
  'inquiry',
  'confirmed',
  'completed',
  'archived',
];

const fallbackStatusOptions = computed(() =>
  DEFAULT_SYSTEM_STATUSES.map(stage => ({
    type: 'system',
    key: stage,
    label: stage.charAt(0).toUpperCase() + stage.slice(1),
    system_stage: stage,
    anchor_system_stage: stage,
    resolved_color: resolveStatusOptionColor({ system_stage: stage }),
  }))
);

const normalizedStatusOptions = computed(() => {
  if (availableStatusOptions.value.length) return availableStatusOptions.value;
  return fallbackStatusOptions.value;
});

// Status options formatted for MultiselectDropdown
const statusOptions = computed(() =>
  normalizedStatusOptions.value.map(option => ({
    ...option,
    id: option.key,
    name: option.label || option.key,
    color: resolveStatusOptionColor(option),
  }))
);

// Selected status as MultiselectDropdown item
const selectedStatus = computed(() => {
  return statusOptions.value.find(o => o.id === status.value) || {};
});

const getStatusPayload = statusKey => {
  const selectedOption = normalizedStatusOptions.value.find(
    option => option.key === statusKey
  );
  const fallbackStage = DEFAULT_SYSTEM_STATUSES.includes(statusKey)
    ? statusKey
    : 'draft';

  if (!selectedOption) {
    return {
      system_stage: fallbackStage,
      custom_status: null,
    };
  }

  if (selectedOption.type === 'custom') {
    return {
      system_stage: selectedOption.system_stage,
      custom_status: selectedOption.key,
    };
  }

  return {
    system_stage: selectedOption.system_stage,
    custom_status: null,
  };
};

const loadStatusOptions = async () => {
  try {
    const response = await store.dispatch('planningEvents/getStatusOptions');
    availableStatusOptions.value = buildOrderedStatusOptions(response);
  } catch {
    availableStatusOptions.value = [];
  }
};

// Users for manager assignment
const users = computed(() => store.getters['agents/getAgents'] || []);

// Manager options formatted for MultiselectDropdown
const managerOptions = computed(() => {
  return users.value.map(u => ({
    id: u.id,
    name: u.name || u.available_name || u.email,
    thumbnail: u.avatar_url || u.thumbnail,
    availability_status: u.availability_status,
  }));
});

// Selected manager as MultiselectDropdown item
const selectedManager = computed(() => {
  if (!managerId.value) return {};
  return managerOptions.value.find(o => o.id === managerId.value) || {};
});

const onClickAssignStatus = item => {
  if (selectedStatus.value.id === item.id) return;
  status.value = item.id;
};

const onClickAssignManager = item => {
  if (managerId.value === item.id) {
    managerId.value = null;
  } else {
    managerId.value = item.id;
  }
};

// Get event for this allocation
const event = computed(() => {
  if (!props.allocation?.event_id) return null;
  return store.getters['planningEvents/getEventById'](
    props.allocation.event_id
  );
});

const getInitialStatusFromAllocation = allocation => {
  if (!allocation) return 'draft';
  const linkedEvent =
    allocation.event_id &&
    store.getters['planningEvents/getEventById'](allocation.event_id);
  return (
    allocation.metadata?.event_status ||
    linkedEvent?.custom_status ||
    linkedEvent?.system_stage ||
    'draft'
  );
};

const statusPayloadMatchesEvent = (statusPayload, linkedEvent) => {
  if (!linkedEvent || !statusPayload) return false;
  const eventCustom = linkedEvent.custom_status || null;
  const payloadCustom = statusPayload.custom_status || null;
  return (
    linkedEvent.system_stage === statusPayload.system_stage &&
    eventCustom === payloadCustom
  );
};

const hydrateEventFields = linkedEvent => {
  if (!linkedEvent) return;
  isHydratingEventData.value = true;
  status.value =
    linkedEvent.custom_status ||
    linkedEvent.system_stage ||
    status.value ||
    'draft';
  managerId.value = linkedEvent.event_manager_id || null;
  nextTick(() => {
    isHydratingEventData.value = false;
  });
};

const fetchAndHydrateLinkedEvent = async eventId => {
  if (!eventId) {
    nextTick(() => {
      isHydratingEventData.value = false;
    });
    return;
  }

  let linkedEvent = store.getters['planningEvents/getEventById'](eventId);
  if (linkedEvent?.custom_status) {
    hydrateEventFields(linkedEvent);
    return;
  }

  try {
    linkedEvent = await store.dispatch('planningEvents/show', eventId);
    if (linkedEvent) {
      hydrateEventFields(linkedEvent);
    } else {
      nextTick(() => {
        isHydratingEventData.value = false;
      });
    }
  } catch {
    if (linkedEvent) {
      hydrateEventFields(linkedEvent);
    } else {
      nextTick(() => {
        isHydratingEventData.value = false;
      });
    }
  }
};

// Calculate submenu position based on parent button position
const calculateSubmenuPosition = submenuType => {
  const buttonRef =
    submenuType === 'status' ? statusButtonRef : managerButtonRef;

  if (!buttonRef?.value) {
    return 'right';
  }

  const buttonRect = buttonRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const submenuWidth = submenuType === 'status' ? 150 : 200; // min-width from CSS

  // Calculate where the right edge of the submenu would be
  const submenuRight = buttonRect.right + submenuWidth;

  // If submenu would overflow right edge, position it to the left
  if (submenuRight > viewportWidth - 16) {
    return 'left';
  }

  return 'right';
};

// Dates Options
const currentYear = new Date().getFullYear();
const years = computed(() => {
  const y = [];
  for (let i = currentYear - 2; i <= currentYear + 5; i += 1) {
    y.push(i);
  }
  return y;
});

const months = computed(() => [
  { value: 0, label: 'Jan' },
  { value: 1, label: 'Feb' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Apr' },
  { value: 4, label: 'May' },
  { value: 5, label: 'Jun' },
  { value: 6, label: 'Jul' },
  { value: 7, label: 'Aug' },
  { value: 8, label: 'Sep' },
  { value: 9, label: 'Oct' },
  { value: 10, label: 'Nov' },
  { value: 11, label: 'Dec' },
]);

const days = computed(() => {
  const d = new Date(startYear.value, startMonth.value + 1, 0).getDate();
  const arr = [];
  for (let i = 1; i <= d; i += 1) {
    arr.push(i);
  }
  return arr;
});

const endDays = computed(() => {
  const d = new Date(endYear.value, endMonth.value + 1, 0).getDate();
  const arr = [];
  for (let i = 1; i <= d; i += 1) {
    arr.push(i);
  }
  return arr;
});

const snapToQuarterHour = dateInput => {
  const d = new Date(dateInput);
  const rounded =
    Math.round(d.getMinutes() / TIME_STEP_MINUTES) * TIME_STEP_MINUTES;
  if (rounded === 60) {
    d.setHours(d.getHours() + 1, 0, 0, 0);
  } else {
    d.setMinutes(rounded, 0, 0);
  }
  return toLocalTime(d);
};

const timeOptions = computed(() => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += TIME_STEP_MINUTES) {
      const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      options.push({
        value,
        label: formatTime(new Date(2000, 0, 1, hour, minute)),
      });
    }
  }
  return options;
});

const categoryOptions = computed(() => {
  return Object.entries(ALLOCATION_CATEGORIES)
    .map(([key, config]) => ({
      value: key,
      label: config.label,
      color: config.color,
      icon: config.icon,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
});

const currentCategoryConfig = computed(() => {
  return getCategoryConfig(category.value);
});

const cycleCategory = () => {
  const currentIndex = categoryOptions.value.findIndex(
    opt => opt.value === category.value
  );
  const nextIndex = (currentIndex + 1) % categoryOptions.value.length;
  category.value = categoryOptions.value[nextIndex].value;
};

const colorSwatches = CUSTOM_COLOR_SWATCHES;
const recentColors = ref(readRecentAllocationColors());
const customSwatches = computed(() => {
  const defaults = new Set(CUSTOM_COLOR_SWATCHES.map(c => c.toLowerCase()));
  return recentColors.value.filter(hex => !defaults.has(hex.toLowerCase()));
});

const selectSwatch = swatch => {
  customColor.value = customColor.value === swatch ? null : swatch;
};

const isDiscoActive = computed(() => customColor.value === DISCO_COLOR);

const toggleDisco = () => {
  customColor.value = isDiscoActive.value ? null : DISCO_COLOR;
};

// ColorPicker only understands hex values; keep the sentinel out of it.
const pickerColor = computed({
  get: () => (isDiscoActive.value ? '' : customColor.value || ''),
  set: value => {
    customColor.value = value || null;
  },
});

const resetColor = () => {
  customColor.value = null;
};

const getStartDate = () => {
  const [hours, minutes] = startTime.value.split(':').map(Number);
  return new Date(
    startYear.value,
    startMonth.value,
    startDay.value,
    hours,
    minutes
  );
};

const getEndDate = () => {
  const [hours, minutes] = endTime.value.split(':').map(Number);
  return new Date(endYear.value, endMonth.value, endDay.value, hours, minutes);
};

const dateStrFromParts = (year, month, day) =>
  formatLocalDate(new Date(year, month, day));

const applyClampedFields = (fields, side) => {
  const clamped = clampAllocationDateTimeFields(fields, side, {
    minDurationMs: TIME_STEP_MINUTES * 60 * 1000,
  });

  const [sYear, sMonth, sDay] = clamped.startDate.split('-').map(Number);
  const [eYear, eMonth, eDay] = clamped.endDate.split('-').map(Number);

  startYear.value = sYear;
  startMonth.value = sMonth - 1;
  startDay.value = sDay;
  startTime.value = clamped.startTime;

  endYear.value = eYear;
  endMonth.value = eMonth - 1;
  endDay.value = eDay;
  endTime.value = clamped.endTime;
};

const currentFieldSnapshot = () => ({
  startDate: dateStrFromParts(
    startYear.value,
    startMonth.value,
    startDay.value
  ),
  startTime: startTime.value,
  endDate: dateStrFromParts(endYear.value, endMonth.value, endDay.value),
  endTime: endTime.value,
});

const startDayModel = computed({
  get: () => startDay.value,
  set: value => {
    applyClampedFields(
      {
        ...currentFieldSnapshot(),
        startDate: dateStrFromParts(startYear.value, startMonth.value, value),
      },
      'start'
    );
  },
});

const startMonthModel = computed({
  get: () => startMonth.value,
  set: value => {
    applyClampedFields(
      {
        ...currentFieldSnapshot(),
        startDate: dateStrFromParts(startYear.value, value, startDay.value),
      },
      'start'
    );
  },
});

const startYearModel = computed({
  get: () => startYear.value,
  set: value => {
    applyClampedFields(
      {
        ...currentFieldSnapshot(),
        startDate: dateStrFromParts(value, startMonth.value, startDay.value),
      },
      'start'
    );
  },
});

const startTimeModel = computed({
  get: () => startTime.value,
  set: value => {
    applyClampedFields(
      { ...currentFieldSnapshot(), startTime: value },
      'start'
    );
  },
});

const endDayModel = computed({
  get: () => endDay.value,
  set: value => {
    applyClampedFields(
      {
        ...currentFieldSnapshot(),
        endDate: dateStrFromParts(endYear.value, endMonth.value, value),
      },
      'end'
    );
  },
});

const endMonthModel = computed({
  get: () => endMonth.value,
  set: value => {
    applyClampedFields(
      {
        ...currentFieldSnapshot(),
        endDate: dateStrFromParts(endYear.value, value, endDay.value),
      },
      'end'
    );
  },
});

const endYearModel = computed({
  get: () => endYear.value,
  set: value => {
    applyClampedFields(
      {
        ...currentFieldSnapshot(),
        endDate: dateStrFromParts(value, endMonth.value, endDay.value),
      },
      'end'
    );
  },
});

const endTimeModel = computed({
  get: () => endTime.value,
  set: value => {
    applyClampedFields({ ...currentFieldSnapshot(), endTime: value }, 'end');
  },
});

// Duration in minutes
const duration = computed(() => {
  const start = getStartDate();
  const end = getEndDate();
  if (!start || !end) return 0;
  return Math.round((end - start) / (1000 * 60));
});

// Format duration display
const durationDisplay = computed(() => {
  const mins = duration.value;
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h ${remainingMins}m`;
});

// Check if this is an edit operation (allocation has an id)
const isEditMode = computed(() => {
  return props.allocation && props.allocation.id;
});

const nameLabel = computed(() => {
  if (props.allocation && props.allocation.parent_allocation_id) {
    return 'Slot name';
  }
  return 'Event name';
});

// Check if this is a parent allocation for an event (title should not be editable)
const isEventParent = computed(() => {
  return (
    props.allocation &&
    props.allocation.event_id &&
    !props.allocation.parent_allocation_id
  );
});

// Watch for allocation changes to populate form
watch(
  () => props.allocation,
  async newAllocation => {
    if (newAllocation) {
      isHydratingEventData.value = true;
      title.value = newAllocation.title || '';

      const start = new Date(newAllocation.start_at);
      const end = new Date(newAllocation.end_at);

      startYear.value = start.getFullYear();
      startMonth.value = start.getMonth();
      startDay.value = start.getDate();
      startTime.value = snapToQuarterHour(start);

      endYear.value = end.getFullYear();
      endMonth.value = end.getMonth();
      endDay.value = end.getDate();
      endTime.value = snapToQuarterHour(end);

      category.value = newAllocation.category || 'event';
      customColor.value = newAllocation.metadata?.color || null;
      recentColors.value = readRecentAllocationColors();
      status.value = getInitialStatusFromAllocation(newAllocation);

      // Populate status and manager from linked event
      if (newAllocation.event_id) {
        await fetchAndHydrateLinkedEvent(newAllocation.event_id);
      } else {
        nextTick(() => {
          isHydratingEventData.value = false;
        });
      }
    }
  },
  { immediate: true }
);

watch(
  event,
  linkedEvent => {
    if (!linkedEvent) return;
    hydrateEventFields(linkedEvent);
  },
  { immediate: true }
);

// Emit real-time updates when form fields change
const emitUpdate = () => {
  if (!props.allocation) return;

  emit('update', {
    title: title.value,
    start_at: getStartDate(),
    end_at: getEndDate(),
    category: category.value,
    resource_id: resolvedResourceId.value,
    buffer_before_min: coerceAllocationInteger(
      props.allocation.buffer_before_min
    ),
    buffer_after_min: coerceAllocationInteger(
      props.allocation.buffer_after_min
    ),
    metadata: {
      ...(props.allocation.metadata || {}),
      color: customColor.value || null,
    },
  });
};

// Calculate position when submenu opens
watch(activeSubmenu, newVal => {
  if (newVal) {
    submenuPositions.value[newVal] = calculateSubmenuPosition(newVal);
  }
});

const handleDelete = () => {
  if (isEditMode.value) {
    emit('delete', props.allocation.id);
    isVisible.value = false;
  }
};

watch(
  [
    title,
    startYear,
    startMonth,
    startDay,
    startTime,
    endYear,
    endMonth,
    endDay,
    endTime,
    category,
    customColor,
  ],
  () => {
    emitUpdate();
  }
);

// Persist custom color changes to the allocation
watch(customColor, async newColor => {
  if (!isEditMode.value) return;
  const savedColor = props.allocation?.metadata?.color || null;
  if ((newColor || null) === savedColor) return;
  try {
    const nextMetadata = {
      ...(props.allocation?.metadata || {}),
      color: newColor || null,
    };
    await store.dispatch('planningAllocations/update', {
      id: props.allocation.id,
      metadata: nextMetadata,
    });
    if (newColor && newColor !== DISCO_COLOR) {
      recentColors.value = pushRecentAllocationColor(newColor);
    }
  } catch (error) {
    // silently handle
  }
});

// Persist status changes to the linked event
watch(status, async (newStatus, oldStatus) => {
  if (isHydratingEventData.value) return;
  if (isEditMode.value && props.allocation?.event_id) {
    try {
      const statusPayload = getStatusPayload(newStatus);
      const linkedEvent =
        event.value ||
        store.getters['planningEvents/getEventById'](props.allocation.event_id);
      if (statusPayloadMatchesEvent(statusPayload, linkedEvent)) return;
      const result = await requestStageUpdate({
        event: linkedEvent || {
          id: props.allocation.event_id,
          metadata: {},
        },
        statusPayload,
        applyUpdate: payload =>
          store.dispatch('planningEvents/update', {
            id: props.allocation.event_id,
            ...payload,
          }),
        onCancel: () => {
          isHydratingEventData.value = true;
          status.value = oldStatus;
          nextTick(() => {
            isHydratingEventData.value = false;
          });
        },
      });
      if (!result) return;
    } catch (error) {
      isHydratingEventData.value = true;
      status.value = oldStatus;
      nextTick(() => {
        isHydratingEventData.value = false;
      });
    }
  }
});

// Persist manager changes to the linked event
watch(managerId, async newManagerId => {
  if (isHydratingEventData.value) return;
  if (isEditMode.value && props.allocation?.event_id) {
    const linkedEvent =
      event.value ||
      store.getters['planningEvents/getEventById'](props.allocation.event_id);
    if ((linkedEvent?.event_manager_id || null) === (newManagerId || null)) {
      return;
    }
    try {
      await store.dispatch('planningEvents/update', {
        id: props.allocation.event_id,
        event_manager_id: newManagerId,
      });
    } catch (error) {
      // silently handle
    }
  }
});

// Modal positioning. Initial placement is computed from the trigger position;
// after the modal mounts we measure the rendered size and reposition so the
// whole modal stays within the viewport (mirroring the Statuses.vue popover).
const VIEWPORT_PADDING = 16;
const modalPosition = reactive({
  top: null,
  left: null,
  bottom: null,
  width: 500,
});

const MODAL_WIDTH = 500;
const MODAL_WIDTH_COMPACT = 410;

const computeInitialPosition = () => {
  const modalWidth = props.compact ? MODAL_WIDTH_COMPACT : MODAL_WIDTH;
  modalPosition.width = modalWidth;
  const viewportWidth = window.innerWidth;

  // Bottom-anchored positioning (e.g. opened from the action bar)
  if (props.position.anchorBottom) {
    modalPosition.left = Math.max(
      VIEWPORT_PADDING,
      Math.round((viewportWidth - modalWidth) / 2)
    );
    modalPosition.top = null;
    modalPosition.bottom = props.position.anchorBottom;
    return;
  }

  let left = props.position.x + VIEWPORT_PADDING;
  let top = props.position.y;

  if (left + modalWidth > viewportWidth - VIEWPORT_PADDING) {
    left = viewportWidth - modalWidth - VIEWPORT_PADDING;
  }
  if (left < VIEWPORT_PADDING) {
    left = VIEWPORT_PADDING;
  }
  if (top < VIEWPORT_PADDING) {
    top = VIEWPORT_PADDING;
  }

  modalPosition.left = left;
  modalPosition.top = top;
  modalPosition.bottom = null;
};

const adjustPositionToFitViewport = () => {
  // No adjustment needed - modal uses top/bottom positioning to fill available space
};

const modalStyle = computed(() => {
  const style = {
    width: `${modalPosition.width}px`,
    top: `${VIEWPORT_PADDING}px`,
    maxHeight: `calc(100vh - ${VIEWPORT_PADDING * 2}px)`,
    left: modalPosition.left != null ? `${modalPosition.left}px` : undefined,
  };

  return style;
});

const open = () => {
  isVisible.value = true;
  loadStatusOptions();
  // Load users for manager assignment if not already loaded
  if (!users.value.length) {
    store.dispatch('agents/get');
  }
};

// Auto-open when embedded
if (props.embedded) {
  isVisible.value = true;
  loadStatusOptions();
  if (!users.value.length) {
    store.dispatch('agents/get');
  }
}

const close = () => {
  isVisible.value = false;
  emit('cancel');
};

const handleConfirm = () => {
  const allocationData = {
    title: title.value,
    start_at: getStartDate(),
    end_at: getEndDate(),
    category: category.value,
    resource_id: resolvedResourceId.value,
    metadata: {
      ...(props.allocation?.metadata || {}),
      color: customColor.value || null,
    },
  };

  // Include id if this is an edit operation
  if (isEditMode.value) {
    allocationData.id = props.allocation.id;
  }

  if (customColor.value && customColor.value !== DISCO_COLOR) {
    recentColors.value = pushRecentAllocationColor(customColor.value);
  }

  emit('confirm', allocationData);
  isVisible.value = false;
};

const handleCancel = () => {
  close();
};

// Context Menu Handlers
const openContextMenu = event => {
  event.stopPropagation();
  contextMenuPosition.value = {
    x: event.clientX,
    y: event.clientY,
  };
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
  activeSubmenu.value = null;
};

const toggleSubmenu = submenuName => {
  if (activeSubmenu.value === submenuName) {
    activeSubmenu.value = null;
  } else {
    activeSubmenu.value = submenuName;
  }
};

const handleShowEvent = () => {
  console.log('handleShowEvent called', {
    allocation: props.allocation,
    eventId: props.allocation?.event_id,
  });
  if (props.allocation?.event_id) {
    closeContextMenu();
    close();
    router.push({
      name: 'planning_events_show',
      params: { eventId: props.allocation.event_id },
      query: calendarEventShowQuery(),
    });
  } else {
    console.warn('No event_id found on allocation');
  }
};

const handleChangeStatus = async statusOption => {
  if (isEditMode.value && props.allocation?.event_id) {
    try {
      const statusPayload = getStatusPayload(statusOption.key);
      const linkedEvent =
        event.value ||
        store.getters['planningEvents/getEventById'](props.allocation.event_id);
      const result = await requestStageUpdate({
        event: linkedEvent || {
          id: props.allocation.event_id,
          metadata: {},
        },
        statusPayload,
        applyUpdate: payload =>
          store.dispatch('planningEvents/update', {
            id: props.allocation.event_id,
            ...payload,
          }),
      });
      if (!result) return;
      isHydratingEventData.value = true;
      status.value = statusOption.key;
      nextTick(() => {
        isHydratingEventData.value = false;
      });
      closeContextMenu();
    } catch (error) {
      console.error('Failed to update event status:', error);
    }
  }
};

const handleAssignManager = async userId => {
  if (isEditMode.value && props.allocation?.event_id) {
    try {
      await store.dispatch('planningEvents/update', {
        id: props.allocation.event_id,
        event_manager_id: userId,
      });
      closeContextMenu();
    } catch (error) {
      console.error('Failed to assign manager:', error);
    }
  }
};

const handleCopyEventLink = async () => {
  if (props.allocation?.event_id) {
    const url = `${window.location.origin}/app/accounts/${store.getters.getCurrentAccountId}/planning/events/${props.allocation.event_id}`;
    try {
      await navigator.clipboard.writeText(url);
      closeContextMenu();
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  }
};

const isContextStatusSelected = statusOption => {
  const linkedEvent = event.value;
  if (!linkedEvent) return false;

  if (linkedEvent.custom_status) {
    return (
      statusOption.type === 'custom' &&
      statusOption.key === linkedEvent.custom_status
    );
  }

  return (
    statusOption.type === 'system' &&
    statusOption.system_stage === linkedEvent.system_stage
  );
};

// Handle click outside to close
const handleClickOutside = () => {
  close();
};

// Handle Escape key to close
const handleEscapeKey = event => {
  if (event.key === 'Escape') {
    close();
  }
};

// Add/remove escape key listener when modal visibility changes,
// and reposition the modal to stay within the viewport once it has rendered.
const handleViewportResize = () => {
  computeInitialPosition();
  nextTick(adjustPositionToFitViewport);
};

watch(isVisible, async visible => {
  if (visible) {
    document.addEventListener('keydown', handleEscapeKey);
    if (!props.embedded) {
      window.addEventListener('resize', handleViewportResize);
      computeInitialPosition();
      await nextTick();
      adjustPositionToFitViewport();
    }
  } else {
    document.removeEventListener('keydown', handleEscapeKey);
    window.removeEventListener('resize', handleViewportResize);
  }
});

// Re-adjust whenever the trigger position changes while open.
watch(
  () => [props.position.x, props.position.y, props.position.anchorBottom],
  async () => {
    if (!isVisible.value || props.embedded) return;
    computeInitialPosition();
    await nextTick();
    adjustPositionToFitViewport();
  }
);

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('keydown', handleEscapeKey);
  window.removeEventListener('resize', handleViewportResize);
});

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body" :disabled="embedded">
    <Transition name="modal">
      <!-- Backdrop -->
      <div
        v-if="isVisible && !embedded"
        class="fixed inset-0 z-[9998] bg-black/10"
        @click="handleClickOutside"
      />
    </Transition>
    <Transition name="modal">
      <div
        v-if="isVisible"
        ref="modalRef"
        :class="[
          embedded
            ? 'flex-1 bg-n-background flex flex-col'
            : 'fixed z-[9999] bg-n-alpha-3 backdrop-blur-[100px] shadow-2xl rounded-xl border border-n-weak transition-all duration-300 flex flex-col',
        ]"
        :style="embedded ? undefined : modalStyle"
        @click.stop
      >
        <!-- Header with more options and delete button (edit mode only) -->
        <div
          v-if="isEditMode"
          class="flex items-start justify-end gap-2 mb-4 px-6 pt-6 flex-shrink-0"
        >
          <h3 class="font-bold text-xl w-full self-center truncate">
            {{ allocation.title }}
          </h3>
          <button
            type="button"
            class="flex items-center justify-center w-10 h-10 transition-colors rounded-lg hover:bg-n-ruby-9/10"
            :title="$t('DIALOG.BUTTONS.DELETE')"
            @click="handleDelete"
          >
            <span class="i-lucide-trash-2 size-5 text-n-ruby-9" />
          </button>
          <button
            type="button"
            class="flex items-center justify-center w-10 h-10 transition-colors rounded-lg hover:bg-n-slate-4"
            :title="$t('PLANNING.ALLOCATIONS.MORE_OPTIONS')"
            @click="openContextMenu"
          >
            <span class="i-lucide-more-vertical size-5 text-n-slate-11" />
          </button>
        </div>

        <!-- Scrollable form area -->
        <div class="flex-1 overflow-y-auto px-6 pb-6">
          <form class="flex flex-col gap-6" @submit.prevent="handleConfirm">
            <div class="flex flex-col gap-4">
              <!-- Slot Name with Category Icon Button -->
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ nameLabel }}
                </label>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg transition-all border border-n-weak hover:border-n-base"
                    :style="{
                      backgroundColor: currentCategoryConfig.color,
                      color: currentCategoryConfig.darkColor,
                    }"
                    :title="`Category: ${currentCategoryConfig.label}`"
                    @click="cycleCategory"
                  >
                    <span
                      class="size-5"
                      :class="`i-lucide-${currentCategoryConfig.icon}`"
                    />
                  </button>
                  <Input
                    id="allocation-title"
                    v-model="title"
                    type="text"
                    placeholder="Enter name"
                    class="flex-1 text-neutral-900"
                  />
                </div>
              </div>

              <!-- Color override -->
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('PLANNING.ALLOCATIONS.COLOR.LABEL') }}
                </label>
                <div class="flex items-center gap-2 flex-wrap">
                  <button
                    v-for="swatch in colorSwatches"
                    :key="swatch"
                    type="button"
                    class="size-8 rounded-full border-2 transition-all"
                    :class="
                      customColor === swatch
                        ? 'border-n-slate-12 scale-110'
                        : 'border-n-weak hover:border-n-slate-8'
                    "
                    :style="{ backgroundColor: swatch }"
                    :title="swatch"
                    @click="selectSwatch(swatch)"
                  />
                </div>
                <div
                  v-if="customSwatches.length"
                  class="flex items-center gap-2 flex-wrap"
                >
                  <button
                    v-for="swatch in customSwatches"
                    :key="`custom-${swatch}`"
                    type="button"
                    class="size-8 rounded-full border-2 transition-all"
                    :class="
                      customColor === swatch
                        ? 'border-n-slate-12 scale-110'
                        : 'border-n-weak hover:border-n-slate-8'
                    "
                    :style="{ backgroundColor: swatch }"
                    :title="swatch"
                    @click="selectSwatch(swatch)"
                  />
                </div>
                <div class="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    class="disco-swatch size-8 rounded-full border-2 transition-all"
                    :class="
                      isDiscoActive
                        ? 'border-n-slate-12 scale-110'
                        : 'border-n-weak hover:border-n-slate-8'
                    "
                    :title="$t('PLANNING.ALLOCATIONS.COLOR.DISCO')"
                    @click="toggleDisco"
                  />
                  <ColorPicker v-model="pickerColor" />
                  <button
                    v-if="customColor"
                    type="button"
                    class="flex items-center justify-center size-10 rounded-full border border-n-weak hover:bg-n-slate-4 transition-colors"
                    :title="$t('PLANNING.ALLOCATIONS.COLOR.RESET')"
                    @click="resetColor"
                  >
                    <span class="i-lucide-rotate-ccw size-4 text-n-slate-11" />
                  </button>
                </div>
              </div>

              <!-- Start date & time -->
              <div class="flex flex-row gap-4">
                <div class="flex flex-col gap-2 min-w-0 flex-1">
                  <label class="text-sm font-medium text-n-slate-12">Start date</label>
                  <div class="flex items-center flex-row gap-2">
                    <select
                      v-model="startDayModel"
                      class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                    >
                      <option v-for="d in days" :key="d" :value="d">
                        {{ d }}
                      </option>
                    </select>
                    <select
                      v-model="startMonthModel"
                      class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                    >
                      <option
                        v-for="m in months"
                        :key="m.value"
                        :value="m.value"
                      >
                        {{ m.label }}
                      </option>
                    </select>
                    <select
                      v-model="startYearModel"
                      class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                    >
                      <option v-for="y in years" :key="y" :value="y">
                        {{ String(y).slice(2) }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="flex flex-col gap-2 w-[140px] shrink-0">
                  <label class="text-sm font-medium text-n-slate-12">Start time</label>
                  <select
                    v-model="startTimeModel"
                    class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                  >
                    <option
                      v-for="option in timeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- End date & time -->
              <div class="flex flex-row gap-4">
                <div class="flex flex-col gap-2 min-w-0 flex-1">
                  <label class="text-sm font-medium text-n-slate-12">End date</label>
                  <div class="flex items-center flex-row gap-2">
                    <select
                      v-model="endDayModel"
                      class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                    >
                      <option v-for="d in endDays" :key="d" :value="d">
                        {{ d }}
                      </option>
                    </select>
                    <select
                      v-model="endMonthModel"
                      class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                    >
                      <option
                        v-for="m in months"
                        :key="m.value"
                        :value="m.value"
                      >
                        {{ m.label }}
                      </option>
                    </select>
                    <select
                      v-model="endYearModel"
                      class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                    >
                      <option v-for="y in years" :key="y" :value="y">
                        {{ String(y).slice(2) }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="flex flex-col gap-2 w-[140px] shrink-0">
                  <label class="text-sm font-medium text-n-slate-12">End time</label>
                  <select
                    v-model="endTimeModel"
                    class="w-full h-10 rounded-lg px-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-3 outline outline-1 outline-n-slate-6 cursor-pointer"
                  >
                    <option
                      v-for="option in timeOptions"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Status & Manager -->
              <template v-if="isEditMode">
                <div class="flex flex-row gap-4">
                  <div class="multiselect-wrap--small w-full">
                    <label
                      class="text-sm font-medium text-n-slate-12 mb-1 block"
                      >Status</label>
                    <MultiselectDropdown
                      :options="statusOptions"
                      :selected-item="selectedStatus"
                      :has-thumbnail="false"
                      multiselector-title="Status"
                      multiselector-placeholder="None"
                      no-search-result="No statuses found"
                      input-placeholder="Search status"
                      @select="onClickAssignStatus"
                    />
                  </div>

                  <div class="multiselect-wrap--small w-full">
                    <label
                      class="text-sm font-medium text-n-slate-12 mb-1 block"
                      >Manager</label>
                    <MultiselectDropdown
                      :options="managerOptions"
                      :selected-item="selectedManager"
                      multiselector-title="Assign manager"
                      multiselector-placeholder="Unassigned"
                      no-search-result="No agents found"
                      input-placeholder="Search agent"
                      @select="onClickAssignManager"
                    />
                  </div>
                </div>
              </template>
            </div>
          </form>
        </div>

        <!-- Footer Actions (fixed) -->
        <div
          class="flex items-center justify-end gap-3 p-4 flex-shrink-0 border-t border-n-weak"
        >
          <Button
            type="button"
            color="slate"
            variant="faded"
            class="!bg-n-slate-3 hover:!bg-n-slate-4 !text-n-slate-11 dark:!text-n-slate-11"
            :label="$t('DIALOG.BUTTONS.CANCEL')"
            @click="handleCancel"
          />
          <Button
            type="submit"
            color="blue"
            :label="isEditMode ? 'Update' : 'Add'"
            @click="handleConfirm"
          />
        </div>
      </div>
    </Transition>

    <!-- Context Menu -->
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="closeContextMenu"
    >
      <div
        class="min-w-[200px] bg-white dark:bg-n-slate-2 rounded-lg shadow-lg border border-n-weak overflow-visible"
      >
        <!-- Show Event -->
        <button
          v-if="allocation?.event_id"
          type="button"
          class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center gap-3 transition-colors"
          @mousedown.prevent="handleShowEvent"
        >
          <span class="i-lucide-external-link size-4 text-n-slate-11" />
          <span>Show event</span>
        </button>

        <!-- Change Status (only if event linked) -->
        <div
          v-if="allocation?.event_id"
          class="relative"
          @mouseleave="activeSubmenu === 'status' && (activeSubmenu = null)"
        >
          <button
            ref="statusButtonRef"
            type="button"
            class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center justify-between transition-colors"
            :class="{
              'bg-n-slate-3': activeSubmenu === 'status',
            }"
            @click.stop="toggleSubmenu('status')"
            @mouseenter="activeSubmenu = 'status'"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-circle-dot size-4 text-n-slate-11" />
              <span>Change status</span>
            </div>
            <span class="i-lucide-chevron-right size-4 text-n-slate-11" />
          </button>
          <!-- Submenu -->
          <div
            v-if="activeSubmenu === 'status'"
            ref="statusSubmenuRef"
            class="absolute top-0 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg border border-n-weak min-w-[150px] overflow-hidden z-10"
            :class="
              submenuPositions.status === 'left' ? 'right-full' : 'left-full'
            "
            :style="
              submenuPositions.status === 'left'
                ? 'margin-right: -1px'
                : 'margin-left: -1px'
            "
            @click.stop
          >
            <button
              v-for="statusOption in normalizedStatusOptions"
              :key="`${statusOption.type}-${statusOption.key}`"
              type="button"
              class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 capitalize transition-colors flex items-center gap-2"
              :class="{
                'bg-n-slate-3 font-medium':
                  isContextStatusSelected(statusOption),
              }"
              @mousedown.prevent="handleChangeStatus(statusOption)"
            >
              <span
                class="w-3 h-3 rounded-full"
                :style="{
                  backgroundColor: resolveStatusOptionColor(statusOption),
                }"
              />
              {{ statusOption.label || statusOption.key }}
            </button>
          </div>
        </div>

        <!-- Assign Manager (only if event linked) -->
        <div
          v-if="allocation?.event_id"
          class="relative"
          @mouseleave="activeSubmenu === 'manager' && (activeSubmenu = null)"
        >
          <button
            ref="managerButtonRef"
            type="button"
            class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center justify-between transition-colors"
            :class="{
              'bg-n-slate-3': activeSubmenu === 'manager',
            }"
            @click.stop="toggleSubmenu('manager')"
            @mouseenter="activeSubmenu = 'manager'"
          >
            <div class="flex items-center gap-3">
              <span class="i-lucide-user-cog size-4 text-n-slate-11" />
              <span>Assign manager</span>
            </div>
            <span class="i-lucide-chevron-right size-4 text-n-slate-11" />
          </button>
          <!-- Submenu -->
          <div
            v-if="activeSubmenu === 'manager'"
            ref="managerSubmenuRef"
            class="absolute top-0 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg border border-n-weak min-w-[200px] max-h-[300px] overflow-y-auto z-10"
            :class="
              submenuPositions.manager === 'left' ? 'right-full' : 'left-full'
            "
            :style="
              submenuPositions.manager === 'left'
                ? 'margin-right: -1px'
                : 'margin-left: -1px'
            "
            @click.stop
          >
            <button
              v-for="user in users"
              :key="user.id"
              type="button"
              class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 transition-colors"
              :class="{
                'bg-n-slate-3 font-medium': event?.event_manager_id === user.id,
              }"
              @mousedown.prevent="handleAssignManager(user.id)"
            >
              {{ user.name }}
            </button>
          </div>
        </div>

        <!-- Copy Link to Event -->
        <button
          v-if="allocation?.event_id"
          type="button"
          class="w-full px-4 py-2.5 text-left text-sm text-n-slate-12 hover:bg-n-slate-3 flex items-center gap-3 transition-colors border-t border-n-weak"
          @mousedown.prevent="handleCopyEventLink"
        >
          <span class="i-lucide-link size-4 text-n-slate-11" />
          <span>Copy link to event</span>
        </button>
      </div>
    </ContextMenu>

    <EventLostCancellationModal
      v-model="showLostCancellationModal"
      :event="pendingLostUpdate?.event || event"
      :is-loading="lostCancellationSaving"
      @confirm="confirmLostCancellation"
      @cancel="cancelLostCancellation"
    />
  </Teleport>
</template>

<style scoped>
.modal-enter-active {
  transition: all 0.2s ease-out;
}

.modal-leave-active {
  transition: all 0.15s ease-in;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.disco-swatch {
  background-image: linear-gradient(
    90deg,
    #ff4d4d,
    #ff9a3c,
    #ffd93d,
    #6bcf63,
    #4dd0e1,
    #5c9bff,
    #a56bff,
    #ff5ce0,
    #ff4d4d
  );
  background-size: 300% 100%;
  animation: discoSwatchShift 4s linear infinite;
}

@keyframes discoSwatchShift {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 300% 50%;
  }
}
</style>
